// Unified legal content. Shared clauses appear once; `byRole` adds role-only items.
// Block types: p | list (items: string or [label, text]) | callout (tone, title?, text?/items?)

export const LEGAL_ROLES = [
  { key: 'learner', label: 'Learner' },
  { key: 'teacher', label: 'Teacher / Trainer / Mentor / Creator' },
  { key: 'seller', label: 'User / Seller / Drop-Shipping' },
];

export const LAST_UPDATED = 'July 2026';
export const CONTACT_EMAIL = 'ieskilldev@gmail.com';

export const PRIVACY_POLICY = [
  {
    title: 'Information We Collect',
    blocks: [
      { type: 'p', text: 'Depending on how you use Insight Evolution, we collect the following information:' },
      {
        type: 'list',
        items: [
          ['Account Information', 'Full name, surname, email address and login credentials.'],
          ['Sign-Up Verification Documents', 'Identity number, identity document, proof of residential address and a facial profile photo (all required), plus any recent qualification certificates you choose to upload (optional, up to 5).'],
          ['Payment Information', 'Payment method details, processed securely through integrated external payment gateways (e.g. PayPal).'],
          ['Usage Data', 'Login timestamps, video watch history, device identifiers, feature usage and platform interactions.'],
          ['Communication Data', 'Messages exchanged with teachers, learners, buyers, community forums and support.'],
        ],
      },
    ],
    byRole: {
      learner: [{ type: 'list', items: [
        ['Learning Data', 'Your student number (STU-YYYY-NNNN), course enrolments, module completion, quiz scores and academic progress.'],
        ['Declarations & Documents Issued', 'Course Acknowledgment Declarations you accept, and the completion certificates and Letters of Model Acquisition issued to you.'],
      ] }],
      teacher: [{ type: 'list', items: [
        ['Teaching & Course Data', 'Course uploads, lesson content, gallery images and captions, course declarations and letters you author, enrolment statistics and completion metrics.'],
        ['Payout Information', 'Payout details and financial account information for disbursing teacher revenue.'],
      ] }],
      seller: [{ type: 'list', items: [
        ['Learning Data', 'Course enrolments, progress and completion status.'],
        ['Seller Data', 'Products listed for sale, sales history and earnings.'],
        ['Business Information', 'Shipping addresses, inventory data and supplier details.'],
      ] }],
    },
  },
  {
    title: 'How We Use Your Information',
    blocks: [
      { type: 'list', items: [
        'To register, maintain and secure your account, and to verify your identity and address from your sign-up documents.',
        'To deliver essential system updates, security alerts, policy changes and administrative notices.',
        'To analyse platform performance and improve features, user experience and security.',
        'To comply with applicable legal, accounting, financial and regulatory requirements.',
      ] },
    ],
    byRole: {
      learner: [{ type: 'list', items: [
        'To process course purchases and transaction billing.',
        'To grant access to enrolled content, track progress and recommend relevant courses.',
        'To issue your completion certificates and Letters of Model Acquisition.',
      ] }],
      teacher: [{ type: 'list', items: [
        'To process course sales, calculate payouts and disburse teacher revenue.',
        'To provide content-creation tools and course analytics.',
      ] }],
      seller: [{ type: 'list', items: [
        'To process course purchases and give access to enrolled courses.',
        'To facilitate product listings in the Insight Warehouse and process sales and payouts.',
      ] }],
    },
  },
  {
    title: 'Information Sharing',
    blocks: [
      { type: 'p', text: 'We do not sell your personal data to third parties. We share it only as necessary with:' },
      { type: 'list', items: [
        ['Payment Processors', 'To securely authorise payments and distribute payouts.'],
        ['Service Providers', 'Cloud hosting, analytics, infrastructure and customer-support vendors bound by confidentiality agreements.'],
        ['Legal Authorities', 'When required by law or court order, or to protect our legitimate legal rights.'],
      ] },
      { type: 'p', text: 'Your identity document and proof of address are used only for verification and compliance and are not displayed on your public profile. Your profile photo is shown with your account.' },
    ],
    byRole: {
      learner: [{ type: 'list', items: [
        ['Teachers', 'Your name, profile details and progress for courses in which you are enrolled.'],
        ['Issued Documents', 'Your name, student number or identity number, teacher name and date appear on the certificates and Letters of Model Acquisition issued to you.'],
      ] }],
      teacher: [
        { type: 'list', items: [['Learners & Visitors', 'Your public profile, credentials, course content and public gallery.']] },
        { type: 'p', text: 'Any requested modifications to teacher-related policies must be submitted in writing via email to Insight Evolution for formal review and written acknowledgment.' },
      ],
      seller: [{ type: 'list', items: [
        ['Teachers', 'Your enrolment status and basic profile for courses you are enrolled in.'],
        ['Buyers', 'Your seller profile, product information and shipping details for their orders.'],
        ['Shipping Partners', 'For order fulfilment and delivery.'],
      ] }],
    },
  },
  {
    title: 'Data Security',
    blocks: [{ type: 'p', text: 'We implement industry-standard technical and administrative security measures, including encryption of sensitive data in transit, secure cloud infrastructure and periodic vulnerability audits. Payment card details are processed directly through PCI-compliant external gateways; Insight Evolution does not store full card numbers on its servers.' }],
  },
  {
    title: 'Cookies and Tracking Technologies',
    blocks: [{ type: 'p', text: 'We use cookies and similar technologies to maintain login sessions, save preferences and collect platform analytics. You can adjust cookie preferences in your browser; disabling certain cookies may limit some platform features.' }],
  },
  {
    title: 'Your Rights',
    blocks: [
      { type: 'p', text: 'Depending on your jurisdiction, you have the right to:' },
      { type: 'list', items: [
        ['Access', 'Request a copy of the personal information we hold about you.'],
        ['Correction', 'Request updates to inaccurate, incomplete or outdated data.'],
        ['Deletion', 'Request removal of your account and personal data, subject to statutory retention exceptions.'],
        ['Portability', 'Export your learning, course or sales history.'],
        ['Opt-Out', 'Unsubscribe from non-essential promotional communications at any time.'],
      ] },
    ],
  },
  {
    title: 'Data Retention',
    blocks: [{ type: 'p', text: 'We retain your personal data while your account is active. After an account deletion request, your data is permanently deleted within 30 days, except where longer retention is required by law for accounting, audit, tax or dispute-resolution purposes.' }],
  },
];

export const GENERAL_TERMS = [
  {
    title: 'Sign-Up & Identity Verification',
    blocks: [
      { type: 'p', text: 'Every account, whatever its type, must be registered with the following:' },
      { type: 'list', items: [
        ['Required', 'Identity number, identity document (PDF, JPEG or PNG), proof of residential address and a facial profile photo.'],
        ['Optional', 'Up to 5 recent qualification certificates.'],
        ['Acceptance', 'Acceptance of this Privacy Policy and these Terms of Service for your account type.'],
      ] },
      { type: 'callout', tone: 'red', text: 'All documents must be genuine, current and your own. Submitting false, altered or third-party documents is grounds for immediate account termination.' },
    ],
  },
  {
    title: 'Account Responsibilities',
    blocks: [{ type: 'list', items: [
      ['Accuracy', 'You must provide accurate, current and complete information during registration.'],
      ['Account Security', 'You are solely responsible for protecting the confidentiality of your login credentials.'],
      ['Non-Transferability', 'Accounts, course access and publishing or selling privileges may not be shared, sold or transferred.'],
      ['Account Locking', 'Accounts may be temporarily locked after five (5) consecutive failed login attempts.'],
    ] }],
    byRole: {
      learner: [{ type: 'list', items: [['Age Requirement', 'You must be at least 13 years old, or have verifiable parent/guardian consent where required by local law.']] }],
      teacher: [{ type: 'list', items: [['Age Requirement', 'You must be at least 18 years old (or the legal age of majority in your jurisdiction) to create a teacher account.']] }],
      seller: [{ type: 'list', items: [['Age Requirement', 'You must be at least 18 years old to sell products on the platform.']] }],
    },
  },
  {
    title: 'Course Access and Usage Rights',
    blocks: [
      { type: 'p', text: 'These rights apply to anyone who enrols in a course, whatever their account type.' },
      { type: 'list', items: [
        ['Personal License', 'Purchased courses are licensed for personal, non-commercial use only (limited, non-exclusive and non-transferable).'],
        ['Prohibited Actions', 'You may not download, copy, record, screen-record, redistribute or re-license course videos, documents or digital assets.'],
        ['Access Duration', 'Access remains active while the course is hosted on the platform. If a teacher removes a course, access to that content may end.'],
        ['Course Acknowledgment Declaration', 'Where a course has an admin-approved Acknowledgment Declaration, you must accept it before purchasing the course or any of its individual lessons.'],
      ] },
      { type: 'callout', tone: 'green', text: 'By enrolling in a course, you accept any additional course-specific terms supplied by its teacher.' },
    ],
  },
  {
    title: 'Digital Content & Non-Refund Policy',
    blocks: [
      { type: 'callout', tone: 'red', title: 'IMPORTANT: Please read carefully before purchasing.' },
      { type: 'list', items: [
        ['Format', 'Insight Evolution tutorials are delivered digitally in audio, video and PDF formats.'],
        ['Non-Refundable', 'Once digital tutorials are purchased, unlocked or made available for streaming/download, all sales are final and non-refundable.'],
        ['Buyer Acknowledgment', 'By purchasing, you confirm you have reviewed the course prospectus, syllabus and company policies.'],
        ['Technical Requirements', 'Buyers are responsible for adequate devices and a stable internet connection.'],
        ['Technical Assistance', 'Access issues must be reported promptly to Insight Evolution support or the teacher via official platform channels.'],
      ] },
    ],
  },
  {
    title: 'Payment Processing',
    blocks: [{ type: 'list', items: [
      'Prices are shown in the designated currency at checkout.',
      'All payments and payouts are securely processed through integrated payment gateways (e.g. PayPal).',
      'Completing a transaction confirms your acceptance of these Terms and the Non-Refund Policy for digital products.',
    ] }],
  },
  {
    title: 'Acceptable Use Policy',
    blocks: [
      { type: 'p', text: 'When using Insight Evolution, you agree not to:' },
      { type: 'list', items: [
        'Harass, abuse, defame or send inappropriate communications to learners, teachers, sellers or staff.',
        'Post fraudulent reviews, misleading course descriptions, false qualifications or deceptive product listings.',
        'Bypass payment systems or access restricted areas without authorisation.',
        'Deploy automated scrapers, bots, crawlers or unauthorised data-collection tools.',
        'Interfere with platform security, network integrity or performance.',
        'Use the platform for any illegal, infringing, unethical or fraudulent activity.',
      ] },
    ],
  },
  {
    title: 'Intellectual Property & Copyrights',
    blocks: [{ type: 'p', text: 'All platform designs, software code, logos, branding and proprietary course content remain the exclusive intellectual property of Insight Evolution or its content creators. You receive only a limited, revocable license to use platform materials as permitted by these Terms. Unauthorised reproduction, derivative works or distribution of any asset, or infringement of third-party rights, is strictly prohibited.' }],
  },
  {
    title: 'Account Termination',
    blocks: [{ type: 'p', text: 'Insight Evolution may suspend or permanently terminate any account, without notice, for violations of these Terms or fraudulent activity. You may delete your account at any time from your account settings. Termination immediately revokes access to enrolled courses and digital materials without refund or compensation.' }],
    byRole: {
      teacher: [{ type: 'p', text: 'Termination also revokes access to teacher management tools and removes your course listings.' }],
      seller: [{ type: 'p', text: 'Accounts may also be terminated for poor seller performance, and pending payouts may be withheld pending investigation.' }],
    },
  },
  {
    title: 'Disclaimer of Warranties',
    blocks: [{ type: 'p', text: 'Course content is authored by independent teachers and reflects their personal professional experience, and marketplace products are supplied by independent sellers. Insight Evolution makes no representations or warranties regarding the accuracy, completeness, quality, safety or legality of third-party course materials or marketplace products. Users are responsible for evaluating teacher credentials, seller reputation and product details independently.' }],
  },
  {
    title: 'Limitation of Liability',
    blocks: [{ type: 'p', text: 'Insight Evolution provides its services and products on an "as is" and "as available" basis without warranties of any kind, express or implied. To the maximum extent permitted by law, Insight Evolution is not liable for indirect, incidental, consequential or punitive damages arising from platform use, system downtime, loss of data, practical application of techniques, or third-party teacher or seller claims. Our total cumulative liability shall not exceed the amount you paid to Insight Evolution in the twelve (12) months preceding the claim.' }],
  },
  {
    title: 'Modifications to Terms',
    blocks: [{ type: 'p', text: 'We may amend these Terms at any time. Material changes will be communicated via email or platform notifications. Continued use of the platform after updates are published constitutes acceptance of the revised Terms.' }],
  },
  {
    title: 'Contact Information',
    blocks: [{ type: 'p', text: `For support, policy questions or to report an issue, contact us through in-app support or email ${CONTACT_EMAIL}.` }],
  },
];

export const ROLE_TERMS = {
  learner: [
    {
      title: 'Health, Safety and Practical Work Guidelines',
      blocks: [
        { type: 'p', text: 'Learners taking practical workshops or applied-skill courses must follow standard industry safety protocols:' },
        { type: 'list', items: [
          ['Personal Protective Equipment (PPE)', 'Always wear protective gloves during hair washing, chemical treatments and product applications, and when working near open sores, skin lesions or bodily fluids. Wear face masks during fumigation, in dusty environments or when exposed to strong chemical fumes.'],
          ['Workplace Safety', 'Ensure your workspace or salon has working fire extinguishers, visible hazard signage, proper ventilation and first-aid kits.'],
          ['Client Monitoring', 'During chemical applications (e.g. relaxers or colour treatments), continuously monitor client comfort. If a client reports burning or severe irritation, rinse the product off immediately, regardless of recommended processing times.'],
          ['Safety Limits', 'Over-processing chemical treatments can cause severe burns and permanent scalp damage. Always read product leaflets, observe package limits and conduct patch tests before application.'],
        ] },
      ],
    },
    {
      title: 'Certification and Accreditation Policy',
      blocks: [
        { type: 'p', text: 'Insight Evolution offers three forms of recognition:' },
        { type: 'callout', tone: 'green', title: 'Company Completion Certificate', items: [
          ['Requirements', 'Issued on completing course modules, passing practical quizzes and uploading the required 1-minute video/photo evidence (before-and-after demonstrations) via the Web App. Your teacher or the admin unlocks the certificate once requirements are met.'],
          ['Format', 'A downloadable PDF showing your name, student number, course and the platform signature.'],
          ['Purpose', 'Proof of completion for practical workshop manuals that build foundational skills (~3-month learning model) for personal development, micro-entrepreneurship or CV enhancement.'],
          ['Limitations', 'Not an official government accreditation, trade test qualification, business license or guarantee of professional licensure.'],
        ] },
        { type: 'callout', tone: 'amber', title: 'Letter of Model Acquisition', items: [
          ['Availability', 'Where a teacher has published an admin-approved letter for a course, enrolled learners can download it from their dashboard.'],
          ['Format', 'A PDF showing your name and surname, identity number, teacher, date and the platform signature.'],
          ['Limitations', 'Confirms the model taught in that course only; it is not an accreditation or license.'],
        ] },
        { type: 'callout', tone: 'blue', title: 'Accredited Trade Test Pathways (Optional)', text: 'Learners seeking formal, government-recognised trade accreditation (e.g. Red Seal or national Trade Test certification) may pursue these independent paths:', items: [
          ['Direct ARPL Route', 'Apply to your national trade testing authority for Recognition of Prior Learning (ARPL) under regional trade test requirements.'],
          ['Insight Evolution Advanced Facilitation Level', 'Specialised preparation modules that help structure your documentation and portfolio toward trade test prerequisites (formal eligibility typically requires 3 years of documented practical experience).'],
          ['Formal Vocational Education', 'Enrol in accredited tertiary or vocational colleges for formal multi-year trade programmes.'],
        ] },
      ],
    },
    {
      title: 'Business Licensing and Legal Compliance',
      blocks: [
        { type: 'p', text: 'In democratic countries anyone may start a business with or without accreditation, provided they abide by that country\'s laws. Learners are solely responsible for complying with all applicable requirements, including:' },
        { type: 'list', items: [
          'Municipal business licenses, permits and zoning permissions.',
          'Local or tribal authority regulations and associated fees.',
          'National business registration and corporate structuring.',
          'Tax compliance, trade laws and labour regulations.',
          'Health, sanitation and safety standards specific to your industry.',
        ] },
      ],
    },
    {
      title: 'Physical Kit Purchases and Phased Cancellation Policy',
      blocks: [
        { type: 'p', text: 'Physical kits involve assembly, custom component sourcing, inventory allocation and logistics, so kit purchases follow a strict phased cancellation structure:' },
        { type: 'callout', tone: 'amber', title: 'Phase 1 (Days 1–2 – Administrative Processing)', text: 'Orders may be cancelled within 48 hours of purchase, subject to a mandatory 5% administrative fee (non-recoverable banking, cross-border and gateway charges).' },
        { type: 'callout', tone: 'amber', title: 'Phase 2 (Day 3 – Order Confirmation)', text: 'Orders are confirmed and queue-locked. Cancellation is still permitted on Day 3, subject to the 5% administrative fee.' },
        { type: 'callout', tone: 'red', title: 'Phase 3 (Day 4 Onward – Assembly and Dispatch)', text: 'Kits enter assembly, third-party component acquisition and courier dispatch. Cancellations, returns and refunds are not permitted from Day 4, even if delivery is refused.' },
        { type: 'p', label: 'Unclaimed Orders', text: 'Refused or unclaimed kits are held at our fulfilment centre for five (5) business days. Insight Evolution may help re-sell the kit to another local buyer (subject to re-consignment courier fees). If unassigned within 5 days, the kit is returned to the original shipping address; the buyer retains ownership to use or privately resell it.' },
      ],
    },
    {
      title: 'Mandatory Unboxing & Damage Verification Protocol',
      blocks: [
        { type: 'p', text: 'To receive replacement parts or assistance for damaged, missing or short-shipped items, submit the following within eight (8) hours of delivery:' },
        { type: 'list', items: [
          ['Packaging Video', 'A continuous 30-second video of the sealed outer box and shipping label before opening.'],
          ['Initial Opening Photo', 'A clear photo taken immediately on opening the top of the box.'],
          ['Small Components Photo', 'All small items arranged together on a flat, visible surface.'],
          ['Large Components Video', 'A 15-second video panning across all large tools and kit items.'],
        ] },
        { type: 'callout', tone: 'red', text: 'Submit all media via the Insight Evolution App or support email within the 8-hour window. Claims without complete proof within this timeframe will be declined.' },
      ],
    },
  ],
  teacher: [
    {
      title: 'Intellectual Property & Patents Declaration',
      blocks: [
        { type: 'callout', tone: 'amber', text: 'Teachers retain intellectual property rights to their proprietary course materials, provided registered patents and exclusive content are declared in writing via email to Insight Evolution upon account creation.' },
        { type: 'p', text: 'Unless an explicit written agreement or copyright affidavit is provided within 7 days of account creation, content published to the platform grants Insight Evolution a standard non-exclusive operating license for distribution.' },
      ],
    },
    {
      title: 'Course Declarations, Letters & Certificates',
      blocks: [{ type: 'list', items: [
        ['Non-Refund Disclosure', 'You must clearly communicate the Non-Refund Policy to prospective learners.'],
        ['Acknowledgment Declarations', 'You may author a per-course Acknowledgment Declaration. It takes effect only after admin approval, and learners must accept it before buying the course or its lessons.'],
        ['Letter of Model Acquisition', 'You may author a per-course letter using the placeholders [name and surname], [Reference], [Teacher] and [Date]. It requires admin approval, and any edit is re-submitted for approval.'],
        ['Certificates', 'You are responsible for unlocking completion certificates only for learners who have met the course requirements.'],
      ] }],
    },
    {
      title: 'Course Materials and Physical Kits',
      blocks: [
        { type: 'callout', tone: 'blue', title: 'Accreditation Notice', text: 'Teachers must explicitly state whether a course or workshop is accredited or non-accredited.' },
        { type: 'list', items: [
          ['Kit Selection', 'During course creation, select the kit checkbox if the workshop requires a physical learning kit. This determines the applicable commission structure.'],
          ['Supply Chain & Management', 'Insight Evolution manages sourcing, assembly and delivery of kits so teachers can focus on instruction. Submit a detailed list of required materials before course launch.'],
          ['Delivery Timelines', 'Kit delivery typically takes 3 to 6 weeks depending on component availability, production schedules and destination. Learners receive status updates.'],
        ] },
      ],
    },
    {
      title: 'Content Responsibility',
      blocks: [{ type: 'callout', tone: 'red', items: [
        'While Insight Evolution performs basic compliance reviews, teachers are solely responsible for ensuring their materials do not violate third-party copyrights, trademarks or patents.',
        'Teachers are fully liable for any copyright infringement, plagiarism or unauthorised content published under their account.',
      ] }],
    },
  ],
  seller: [
    {
      title: 'Selling in the Insight Warehouse',
      blocks: [
        { type: 'callout', tone: 'blue', text: 'As a User/Seller/Drop Shipper, you may list and sell products in the Insight Warehouse marketplace.' },
        { type: 'list', items: [
          'All products require admin approval before being listed publicly.',
          'Provide accurate product descriptions, images and pricing.',
          'You are responsible for fulfilling orders, shipping physical products and product quality.',
          'Do not sell counterfeit, stolen, illegal or prohibited items.',
          'You are responsible for any taxes applicable to your sales.',
        ] },
      ],
    },
    {
      title: 'Drop-Shipping Terms',
      blocks: [{ type: 'list', items: [
        'You may list products from third-party suppliers, and must maintain reliable, valid supplier agreements and inventory data.',
        'Accurately represent shipping times and product availability.',
        'You are responsible for handling returns and customer complaints.',
        'Late shipments or quality issues may result in account penalties.',
      ] }],
    },
    {
      title: 'Seller Payouts',
      blocks: [{ type: 'list', items: [
        'Payouts follow the platform payout schedule, after Insight Evolution\'s commission (at platform rates) is deducted from each sale.',
        'You must provide accurate banking information for payouts.',
        'Refund requests on marketplace orders are handled according to the platform refund policy.',
      ] }],
    },
    {
      title: 'Product Rights & Liability',
      blocks: [
        { type: 'p', text: 'You warrant that any products you list are your own creations or that you are properly licensed or authorised to sell them.' },
        { type: 'callout', tone: 'amber', text: 'Insight Evolution may remove products that infringe intellectual property rights and may suspend accounts for repeated violations.' },
        { type: 'callout', tone: 'red', text: 'Sellers are fully liable for their products, including any damages, injuries or losses resulting from their use.' },
      ],
    },
  ],
};

export const DECLARATIONS = {
  learner: {
    title: 'Learner Acknowledgment Statement',
    paragraphs: [
      'I, [Full Name], confirm that I have read, understood and agree to the Insight Evolution Privacy Policy, the General Terms and the Learner Terms, including the Digital Non-Refund Policy and the Phased Physical Kit Purchase & Delivery Terms.',
      'I confirm that the identity document, proof of address, profile photo and any qualification certificates I submitted at sign-up are genuine, current and my own.',
      'I understand that I must accept a course\'s approved Acknowledgment Declaration before purchasing it or its lessons, and that completion certificates and Letters of Model Acquisition are not government accreditations or licenses.',
      'I agree to abide by all health, safety and certification guidelines. I accept that digital content and custom physical kits are non-refundable, and I will resolve any kit allocation or resale independently at my own discretion where applicable.',
    ],
  },
  teacher: {
    title: 'Teacher Acknowledgment Statement',
    paragraphs: [
      'I, [Full Name], confirm that I have read, understood and agree to the Insight Evolution Privacy Policy, the General Terms and the Teacher Terms.',
      'I confirm that the identity document, proof of address, profile photo and any qualification certificates I submitted at sign-up are genuine, current and my own, and that the qualifications I present to learners are true.',
      'I will declare any registered patents or exclusive content in writing within 7 days of account creation, and I accept full liability for the originality of the content I publish.',
      'I understand that my course Acknowledgment Declarations and Letters of Model Acquisition take effect only after admin approval, and that I must disclose the Non-Refund Policy to learners.',
    ],
  },
  seller: {
    title: 'User / Seller / Drop-Shipping Acknowledgment Statement',
    paragraphs: [
      'I, [Full Name], confirm that I have read, understood and agree to the Insight Evolution Privacy Policy, the General Terms and the Seller Terms.',
      'I confirm that the identity document, proof of address, profile photo and any qualification certificates I submitted at sign-up are genuine, current and my own.',
      'I confirm that I own or am authorised to sell every product I list, that my product, pricing and shipping information is accurate, and that I accept full liability for my products and drop-shipping fulfilment.',
    ],
  },
};
